import React, { useState, useCallback, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import ProfileField from './profile/ProfileField';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import Separator from '../../../../commonComponents/forms/Separator';
import CurrencyProp from '../../propTypes/currencyProp';
import ToggleSwitch from '../../../../commonComponents/forms/inputs/ToggleSwitch';
import { compose } from '../../../../services/utilityFunctions';
import DirectSellingModel from '../../../../commonComponents/DirectSellingModel';
import OrganizationDirectSelling from '../../../../commonComponents/propTypes/OrganizationDirectSelling';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import ToggleSwitchWithLabel from '../../../../commonComponents/forms/inputs/ToggleSwitchWithLabel';

function stateFromProps(member = {}) {
  return {
    password: '',
    newPassword: '',
    confirmPassword: '',
    language: member.language || '',
    currency: member.currency || '',
    shippingCountry: member.shippingCountry || '',
    directSelling: member.directSelling
      ? member.directSelling
      : { getOrderCommissionsByCoupon: false },
    isHelpDesk: member.isHelpDesk || false,
  };
}

function SettingsForm({
  t,
  member,
  languages,
  currencies,
  onMemberChange,
  countries,
  directSellingModel,
}) {
  const { organization } = useContext(ConfigsContext);
  const opportunitiesServiceEnabled =
    organization?.services?.opportunities?.enabled;
  const [state, setState] = useState(stateFromProps(member));
  useEffect(() => setState(stateFromProps(member)), [member]);

  const formId = 'passwordForm';
  const validationRules = {
    new_password: { required: false, minlength: 6 },
    confirm_password: { equalTo: '#newPassword' },
  };

  const handleSubmit = async () => {
    const changingPassword = !!state.newPassword;
    const params = {
      password: changingPassword ? state.password : undefined,
      newPassword: changingPassword ? state.newPassword : undefined,
      confirmPassword: undefined,
      language: state.language,
      currency: state.currency,
      shippingCountry:
        state?.shippingCountry?.length > 0 ? state.shippingCountry : null,
      directSelling: state.directSelling,
    };
    onMemberChange(params);
  };

  const handleChange = useCallback((field, value) => {
    if (field === 'getOrderCommissionsByCoupon') {
      setState(prevState => ({
        ...prevState,
        directSelling: {
          getOrderCommissionsByCoupon: value,
        },
      }));
    } else {
      setState(prevState => ({ ...prevState, [field]: value }));
    }
  }, []);

  const inputProps = useCallback(field => {
    switch (field) {
      case 'language':
        return { items: languages.map(l => ({ id: l.code, text: l.native })) };
      case 'currency':
        return { items: currencies.map(c => ({ id: c.code, text: c.name })) };
      case 'shippingCountry':
        return {
          items: countries.map(c => ({
            id: c.code,
            text: `${c.flagEmoji} ${c.name}`,
          })),
        };
      default:
        return undefined;
    }
  }, []);

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id={formId}
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            {opportunitiesServiceEnabled && (
              <div className="settings-opportunities">
                <FormSubtitle title={t('commissions.marketplace')} />
                <ToggleSwitchWithLabel
                  id="isHelpDesk"
                  name="isHelpDesk"
                  label={t('profile.settings.isHelpDesk')}
                  value={state.isHelpDesk}
                  onChange={value => handleChange('isHelpDesk', value)}
                  wrapperClass="form-group m-form__group row"
                  labelClass="col-form-label col-2"
                  inputClassWrapper="col-10"
                  disabled
                />
              </div>
            )}
            {directSellingModel ===
              OrganizationDirectSelling.DIRECT_SELLING_TYPE.WAKEUP && (
              <>
                <FormSubtitle title={t('profile_marketplace_direct_selling')} />
                <ToggleSwitch
                  id="catalog-public"
                  name="catalog-public"
                  label={t('profile_getOrderCommissionsByCoupon')}
                  value={
                    state.directSelling?.getOrderCommissionsByCoupon === true
                  }
                  onChange={value =>
                    handleChange('getOrderCommissionsByCoupon', value)
                  }
                  className="form-group m-form__group row"
                  toggleClasses="col-10"
                  labelClasses="col-form-label col-2"
                  hint={
                    state.directSelling?.getOrderCommissionsByCoupon === true
                      ? t('profile_getOrderCommissionsByCoupon_hint_enabled')
                      : t('profile_getOrderCommissionsByCoupon_hint_disabled')
                  }
                />
                <Separator />
              </>
            )}
            <FormSubtitle title={t('profile_localePreferences')} />
            <ProfileField
              field="language"
              type="select"
              value={state.language}
              onChange={handleChange}
              inputProps={inputProps}
            />
            <ProfileField
              field="currency"
              type="select"
              value={state.currency}
              onChange={handleChange}
              inputProps={inputProps}
              hint={t('profile_currencyHelp')}
              disabled={member.currencyIsBlocked}
            />
            <ProfileField
              field="shippingCountry"
              type="select"
              value={state.shippingCountry}
              onChange={handleChange}
              inputProps={inputProps}
              hint={t('profile_shippingCountryHint')}
            />
            <Separator />
            <FormSubtitle title={t('profile_changePassword')} />
            <ProfileField
              field="password"
              value={state.password}
              onChange={handleChange}
              type="password"
              inputProps={inputProps}
            />
            <ProfileField
              field="newPassword"
              value={state.newPassword}
              onChange={handleChange}
              type="password"
              inputProps={inputProps}
            />
            <ProfileField
              field="confirmPassword"
              value={state.confirmPassword}
              onChange={handleChange}
              type="password"
              inputProps={inputProps}
            />
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

SettingsForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  directSellingModel: PropTypes.string,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  member: PropTypes.shape(UserProp.propType).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
};

SettingsForm.defaultProps = {
  directSellingModel: null,
};

export default compose(DirectSellingModel, translate)(SettingsForm);
